import { TrackedObject } from 'tracked-built-ins';
import { gt } from 'ember-truth-helpers';
import { htmlSafe } from '@ember/template';
import { modifier } from 'ember-modifier';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import { waitForLater, waitForNext } from 'ember-simplepractice/utils/waiters';
import Component from '@glimmer/component';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import styles from './messaging-carousel.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const PANEL_TRANSITION_MS = 200;

export default class MessagingCarousel extends Component {
  @service intl;

  #state = new TrackedObject({
    selectedIndex: 0,
  });

  get items() {
    return this.args.items ?? [];
  }

  get length() {
    return this.items.length;
  }

  get activeIndex() {
    return this.#clampIndex(this.#state.selectedIndex);
  }

  get ariaLabel() {
    return this.args.ariaLabel ?? this.intl.t('emc.carousel.default_aria_label');
  }

  get ariaLive() {
    return this.args.ariaLive ?? 'off';
  }

  get counterText() {
    return `${this.activeIndex + 1}/${this.length}`;
  }

  get isTransitioning() {
    return this.transitionToIndex.isRunning;
  }

  get isPreviousDisabled() {
    return this.activeIndex <= 0 || this.isTransitioning;
  }

  get isNextDisabled() {
    return this.activeIndex >= this.length - 1 || this.isTransitioning;
  }

  get trackStyle() {
    return htmlSafe(
      `--active-index: ${this.activeIndex}; --panel-transition-duration: ${PANEL_TRANSITION_MS}ms;`
    );
  }

  handleItemsChange = modifier(async () => {
    // Necessary to prevent single computation errors since we are potentially modifying
    // the same property we are reading.
    await waitForNext();

    // Prevents the selected index from becoming invalid due to underlying array changes.
    const clamped = this.#clampIndex(this.#state.selectedIndex);
    if (clamped !== this.#state.selectedIndex) {
      this.#state.selectedIndex = clamped;
    }
  });

  panelAriaHidden = index => {
    return index === this.activeIndex ? undefined : 'true';
  };

  panelInert = index => {
    return index !== this.activeIndex;
  };

  goToPrevious = () => {
    if (this.isPreviousDisabled) {
      return;
    }
    this.transitionToIndex.perform(this.activeIndex - 1);
  };

  goToNext = () => {
    if (this.isNextDisabled) {
      return;
    }
    this.transitionToIndex.perform(this.activeIndex + 1);
  };

  #clampIndex(index) {
    if (this.length === 0) {
      return 0;
    }
    return Math.min(Math.max(0, index), this.length - 1);
  }

  transitionToIndex = task({ drop: true }, async index => {
    const clamped = this.#clampIndex(index);
    if (clamped === this.activeIndex) {
      return;
    }

    this.#state.selectedIndex = clamped;

    await waitForLater(PANEL_TRANSITION_MS);
  });

  <template>
    {{#if (gt this.length 0)}}
      <div
        class="{{styles.component}} messaging-carousel"
        aria-label={{this.ariaLabel}}
        role="region"
        data-test-messaging-carousel
        {{this.handleItemsChange this.items}}
        ...attributes
      >
        <div class="viewport" aria-live={{this.ariaLive}}>
          <div class="track" style={{this.trackStyle}}>
            {{#each this.items as |item index|}}
              <div
                class="panel"
                aria-hidden={{this.panelAriaHidden index}}
                inert={{this.panelInert index}}
                data-test-panel
              >
                {{yield item}}
              </div>
            {{/each}}
          </div>
        </div>

        {{#if (gt this.length 1)}}
          <div class="controls" data-test-controls>
            <GhostButton
              @disabled={{this.isPreviousDisabled}}
              @fitContent={{true}}
              @iconOnly={{true}}
              aria-label={{t "emc.carousel.previous"}}
              {{on "click" this.goToPrevious}}
              class="back-button"
              data-test-previous
            >
              {{svgJar "messaging-chevron-left"}}
            </GhostButton>

            <span class="counter" data-test-counter>
              {{this.counterText}}
            </span>

            <GhostButton
              @disabled={{this.isNextDisabled}}
              @fitContent={{true}}
              @iconOnly={{true}}
              aria-label={{t "emc.carousel.next"}}
              {{on "click" this.goToNext}}
              class="next-button"
              data-test-next
            >
              {{svgJar "messaging-chevron-right"}}
            </GhostButton>
          </div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
